import { useCallback, useRef, useState } from 'react';

export type ConnectionRetryState = {
  reconnectionStatus: boolean | undefined;
  isCheckingConnection: boolean;
  handleCheckConnection: () => Promise<boolean | undefined>;
  resetReconnectionStatus: () => void;
};

export function useConnectionRetry(
  checkConnection: () => Promise<boolean>,
): ConnectionRetryState {
  const [reconnectionStatus, setReconnectionStatus] = useState<
    boolean | undefined
  >(undefined);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);

  const isCheckingConnectionRef = useRef(false);

  const resetReconnectionStatus = useCallback(() => {
    setReconnectionStatus(undefined);
  }, []);

  const handleCheckConnection = useCallback(async () => {
    if (isCheckingConnectionRef.current) return undefined;
    isCheckingConnectionRef.current = true;
    setIsCheckingConnection(true);
    setReconnectionStatus(undefined);

    try {
      const isConnected = await checkConnection();
      setReconnectionStatus(isConnected);
      return isConnected;
    } catch {
      setReconnectionStatus(false);
      return false;
    } finally {
      setIsCheckingConnection(false);
      isCheckingConnectionRef.current = false;
    }
  }, [checkConnection]);

  return {
    reconnectionStatus,
    isCheckingConnection,
    handleCheckConnection,
    resetReconnectionStatus,
  };
}
